import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'differenceBarLine',
  name_en: 'differenceBarLine',
  //   name_zh: '差分折柱图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: (amount: number) => amount > 0 && amount <= 10,
    x: (amount: number) => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.ARRAY
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string[];
  };
}

const differenceBarLine = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec) {
    const { data: sourceData } = api;
    const {
      dataIndex = 0,
      encoding: { x, y }
    } = spec;
    const xAxis = sourceData[0].values.map(item => item[x]);
    const yAxisMainData = sourceData[0].values.map(item => item[y[0]]);
    const yAxisSubData = sourceData[0].values.map(item => item[y[1]]);

    // * -------------------------- chart
    return {
      tooltip: {
        trigger: 'axis'
      },
      xAxis: [
        {
          type: 'category',
          show: true,
          data: xAxis
        },
        {
          type: 'category',
          show: false
        }
      ],
      axisPointer: {
        triggerEmphasis: true
      },
      yAxis: [
        {
          __dataIndex: dataIndex,
          __encodeField: y,
          type: 'value',
          show: true
        }
      ],
      series: [
        {
          name: y[0],
          type: 'line',
          showSymbol: false,
          symbol: 'diamond',
          symbolSize: 10,
          data: yAxisMainData
        },
        {
          name: y[1],
          type: 'line',
          data: yAxisSubData,
          showSymbol: false,
          symbol: 'diamond',
          symbolSize: 10,
          lineStyle: {
            type: 'dashed',
            opacity: 0.4,
            width: 1
          }
        },
        {
          name: y[1],
          type: 'custom',
          renderItem(params: any, api: any) {
            const xValue = api.value(0);
            const sonyValue = api.value(1);
            const fujiValue = api.value(2);
            const diff = fujiValue - sonyValue;

            const x = api.coord([xValue, sonyValue])[0];
            const yBase = api.coord([xValue, sonyValue])[1];
            const yTarget = api.coord([xValue, fujiValue])[1];

            const barWidth = api.size([1, 0])[0] * 0.6 > 12 ? 12 : api.size([1, 0])[0] * 0.6;

            return {
              type: 'rect',
              shape: {
                x: x - barWidth / 2,
                y: diff >= 0 ? yTarget : yBase,
                width: barWidth,
                height: Math.abs(yTarget - yBase)
              },
              style: {
                fill: diff >= 0 ? '#3DB4CB' : 'rgba(61,180,203,0.16)',
                lineWidth: 1,
                stroke: '#3DB4CB'
              },
              // 动画：柱子从索尼线位置长出来
              transition: ['shape'],
              enterFrom: {
                shape: {
                  x: x - barWidth / 2,
                  y: yBase,
                  width: barWidth,
                  height: 0
                }
              }
            };
          },
          data: xAxis.map((c, i) => [c, yAxisMainData[i], yAxisSubData[i]])
        }
      ]
    };
  }
});

export { differenceBarLine as layer };
